import React, { useState } from "react";
import {
  X,
  Calendar,
  DollarSign,
  ArrowRight,
  User,
  FileText,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";

import "./DetalhesDepositoModal.scss";

const formatDateLong = (date) => {
  if (!date) return "";

  const d = new Date(date);

  // Se a data vier como string YYYY-MM-DD, tratar localmente
  if (typeof date === "string" && !date.includes("T")) {
    const [year, month, day] = date.split("-");
    return new Date(year, month - 1, day).toLocaleDateString("pt-PT", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  // Para datas com horário, ajustar fuso
  const adjustedDate = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
  return adjustedDate.toLocaleDateString("pt-PT", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatDateTime = (date) => {
  if (!date) return "";

  const d = new Date(date);

  // Para datas com horário, ajustar fuso
  const adjustedDate = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
  return adjustedDate.toLocaleString("pt-PT", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Modal de Detalhes do Depósito
const DetalhesDepositoModal = ({ deposito, onClose }) => {
  if (!deposito) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "CVE",
    }).format(value);
  };

  const formatDate = (date) => {
    const d = new Date(date);

    // Se a data vier como string YYYY-MM-DD, tratar localmente
    if (typeof date === "string" && !date.includes("T")) {
      const [year, month, day] = date.split("-");
      return new Date(year, month - 1, day).toLocaleDateString("pt-PT");
    }

    // Para datas com horário, ajustar para evitar problemas de fuso
    const adjustedDate = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString("pt-PT");
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("pt-PT", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="depositoCaixa-modal-overlay">
      <div className="depositoCaixa-modal depositoCaixa-modal-wide">
        <div className="depositoCaixa-modal-header">
          <div className="depositoCaixa-modal-header-content">
            <h2 className="depositoCaixa-modal-title">Detalhes do Depósito</h2>
            <button onClick={onClose} className="depositoCaixa-modal-close">
              <X className="depositoCaixa-icon" />
            </button>
          </div>
        </div>

        <div className="depositoCaixa-modal-content">
          {/* Status do Depósito */}
          <div className="depositoCaixa-status-section">
            <div
              className={`depositoCaixa-status-badge ${
                deposito.cancelado
                  ? "depositoCaixa-status-cancelled"
                  : "depositoCaixa-status-active"
              }`}
            >
              {deposito.cancelado ? (
                <>
                  <X className="depositoCaixa-icon-sm" />
                  Cancelado
                </>
              ) : (
                <>
                  <CheckCircle className="depositoCaixa-icon-sm" />
                  Ativo
                </>
              )}
            </div>
          </div>

          {/* Informações Principais */}
          <div className="depositoCaixa-details-grid">
            <div className="depositoCaixa-detail-card">
              <div className="depositoCaixa-detail-icon depositoCaixa-detail-icon-blue">
                <DollarSign className="depositoCaixa-icon-lg" />
              </div>
              <div className="depositoCaixa-detail-content">
                <p className="depositoCaixa-detail-label">Valor</p>
                <p className="depositoCaixa-detail-value">
                  {formatCurrency(deposito.valor)}
                </p>
              </div>
            </div>

            <div className="depositoCaixa-detail-card">
              <div className="depositoCaixa-detail-icon depositoCaixa-detail-icon-green">
                <Calendar className="depositoCaixa-icon-lg" />
              </div>
              <div className="depositoCaixa-detail-content">
                <p className="depositoCaixa-detail-label">Data do Depósito</p>
                <p className="depositoCaixa-detail-value">
                  {formatDateLong(deposito.dataDeposito)}
                </p>
              </div>
            </div>
          </div>

          {/* Fluxo de Transferência */}
          <div className="depositoCaixa-transfer-flow">
            <div className="depositoCaixa-transfer-step">
              <div className="depositoCaixa-caixa-box depositoCaixa-caixa-origem">
                <h4>Origem</h4>
                <p>{deposito.caixaOrigem.caixa}</p>
              </div>
            </div>

            <div className="depositoCaixa-transfer-arrow">
              <ArrowRight className="depositoCaixa-icon-lg" />
              <span className="depositoCaixa-transfer-amount">
                {formatCurrency(deposito.valor)}
              </span>
            </div>

            <div className="depositoCaixa-transfer-step">
              <div className="depositoCaixa-caixa-box depositoCaixa-caixa-destino">
                <h4>Destino</h4>
                <p>{deposito.caixaDestino.caixa}</p>
              </div>
            </div>
          </div>

          {/* Informações Adicionais */}
          <div className="depositoCaixa-info-section">
            <div className="depositoCaixa-info-grid">
              <div className="depositoCaixa-info-item">
                <User className="depositoCaixa-icon" />
                <div>
                  <p className="depositoCaixa-info-label">Criado por</p>
                  <p className="depositoCaixa-info-value">
                    {deposito.criadoPor?.name || "N/A"}
                  </p>
                </div>
              </div>

              <div className="depositoCaixa-info-item">
                <Calendar className="depositoCaixa-icon" />
                <div>
                  <p className="depositoCaixa-info-label">Data de criação</p>
                  <p className="depositoCaixa-info-value">
                    {formatDateTime(deposito.created_at)}
                  </p>
                </div>
              </div>

              <div className="depositoCaixa-info-item">
                <FileText className="depositoCaixa-icon" />
                <div>
                  <p className="depositoCaixa-info-label">ID do Depósito</p>
                  <p className="depositoCaixa-info-value depositoCaixa-info-id">
                    {deposito._id}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Observações */}
          {deposito.observacoes && (
            <div className="depositoCaixa-observations">
              <h4 className="depositoCaixa-observations-title">Observações</h4>
              <div className="depositoCaixa-observations-content">
                <p>{deposito.observacoes}</p>
              </div>
            </div>
          )}

          {/* Informações de Cancelamento */}
          {deposito.cancelado && (
            <div className="depositoCaixa-cancellation-info">
              <div className="depositoCaixa-alert depositoCaixa-alert-warning">
                <AlertTriangle className="depositoCaixa-icon" />
                <div>
                  <p className="depositoCaixa-alert-title">
                    Depósito Cancelado
                  </p>
                  <p className="depositoCaixa-alert-text">
                    Cancelado em {formatDate(deposito.canceladoPor?.data)} por{" "}
                    {deposito.canceladoPor?.user?.nome}
                  </p>
                  {deposito.motivoCancelamento && (
                    <p className="depositoCaixa-alert-reason">
                      Motivo: {deposito.motivoCancelamento}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Comprovativo */}
          {deposito.comprovativo?.ficheiro && (
            <div className="depositoCaixa-attachment">
              <h4 className="depositoCaixa-attachment-title">Comprovativo</h4>
              <div className="depositoCaixa-attachment-item">
                <FileText className="depositoCaixa-icon" />
                <div>
                  <p className="depositoCaixa-attachment-name">
                    {deposito.comprovativo.descricao ||
                      "Comprovativo de depósito"}
                  </p>
                  <button className="depositoCaixa-attachment-download">
                    Baixar arquivo
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="depositoCaixa-modal-actions">
          <button
            onClick={onClose}
            className="depositoCaixa-btn depositoCaixa-btn-outline"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal de Confirmação de Cancelamento (estilo MUI)
const ConfirmacaoCancelamentoModal = ({
  open,
  onClose,
  onConfirm,
  deposito,
}) => {
  const [motivo, setMotivo] = useState("");

  if (!open) return null;

  const handleConfirm = () => {
    onConfirm(deposito._id, motivo);
    setMotivo("");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "CVE",
    }).format(value);
  };

  return (
    <div className="depositoCaixa-dialog-overlay">
      <div className="depositoCaixa-dialog">
        <div className="depositoCaixa-dialog-header">
          <div className="depositoCaixa-dialog-icon depositoCaixa-dialog-icon-warning">
            <AlertTriangle className="depositoCaixa-icon-lg" />
          </div>
          <h3 className="depositoCaixa-dialog-title">Cancelar Depósito</h3>
          <p className="depositoCaixa-dialog-subtitle">
            Esta ação não pode ser desfeita
          </p>
        </div>

        <div className="depositoCaixa-dialog-content">
          <div className="depositoCaixa-dialog-info">
            <p className="depositoCaixa-dialog-question">
              Tem certeza que deseja cancelar este depósito?
            </p>

            <div className="depositoCaixa-dialog-details">
              <div className="depositoCaixa-dialog-detail">
                <span className="depositoCaixa-dialog-label">Valor:</span>
                <span className="depositoCaixa-dialog-value">
                  {formatCurrency(deposito?.valor || 0)}
                </span>
              </div>
              <div className="depositoCaixa-dialog-detail">
                <span className="depositoCaixa-dialog-label">De:</span>
                <span className="depositoCaixa-dialog-value">
                  {deposito?.caixaOrigem?.caixa}
                </span>
              </div>
              <div className="depositoCaixa-dialog-detail">
                <span className="depositoCaixa-dialog-label">Para:</span>
                <span className="depositoCaixa-dialog-value">
                  {deposito?.caixaDestino?.caixa}
                </span>
              </div>
            </div>
          </div>

          <div className="depositoCaixa-form-group">
            <label className="depositoCaixa-label">
              Motivo do cancelamento (opcional)
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="depositoCaixa-textarea"
              rows="3"
              placeholder="Descreva o motivo do cancelamento..."
            />
          </div>
        </div>

        <div className="depositoCaixa-dialog-actions">
          <button
            onClick={onClose}
            className="depositoCaixa-btn depositoCaixa-btn-outline"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            className="depositoCaixa-btn depositoCaixa-btn-danger"
          >
            Confirmar Cancelamento
          </button>
        </div>
      </div>
    </div>
  );
};

export { DetalhesDepositoModal, ConfirmacaoCancelamentoModal };
